import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Search, FolderPlus, CheckCircle, ArrowRight } from 'lucide-react';

export const HowItWorks = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const steps = [
    {
      number: '01',
      icon: Search,
      title: 'Browse the Directory',
      description:
        'Filter 500+ vetted sites by niche, DA, link type, and submission method. Select the ones that match your target.',
      color: '#3B82F6',
    },
    {
      number: '02',
      icon: FolderPlus,
      title: 'Create a Project',
      description:
        'Add your target URL, anchor text, and relevant backlink sources from the directory — or add your own custom URLs. Organize campaigns across pages or clients.',
      color: '#8B5CF6',
    },
    {
      number: '03',
      icon: CheckCircle,
      title: 'Track to Publication',
      description:
        'Log outreach status per link. Watch your dashboard update as links move from Submitted to Published. See your full success rate at all times.',
      color: '#22C55E',
    },
  ];

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="section-light py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            3 Steps to Your First{' '}
            <span className="gradient-text">Published Backlink</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Getting started with Searchefy is simple. Here's how to go from zero to your first published backlink.
          </p>
        </motion.div>

        <div className="relative">
          {/* Connection line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 -translate-y-1/2 z-0" />

          <div className="grid lg:grid-cols-3 gap-8 relative z-10">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="relative"
                data-testid={`step-card-${index}`}
              >
                {/* Card */}
                <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
                  {/* Step number */}
                  <div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 text-white font-display text-2xl font-bold"
                    style={{ backgroundColor: step.color }}
                  >
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div
                    className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ml-4"
                    style={{ backgroundColor: `${step.color}15` }}
                  >
                    <step.icon size={24} style={{ color: step.color }} />
                  </div>

                  {/* Content */}
                  <h3 className="font-display text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Arrow connector (mobile) */}
                {index < steps.length - 1 && (
                  <div className="flex justify-center my-4 lg:hidden">
                    <ArrowRight size={24} className="text-gray-300 rotate-90" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <a
            href="https://checkout.dodopayments.com/buy/pdt_0Nc1Sd7RjzAPh38CNHxE9?quantity=1"
            data-testid="how-it-works-cta"
            className="btn-primary inline-flex items-center gap-2"
          >
            <span>Start Building Backlinks — $9</span>
            <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
